import {
  MissionDetail,
  MissionDetail_MissionBadge_BadgeStyle,
  MissionDetail_MissionStatus,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/mission.types';
import {useTrackComponent, useTrackFeature} from '@aboutyou/tracking';
import {
  COLORS,
  GenericIconName,
  Icon,
  Image,
  ProgressBar,
  SPACING,
  Text,
  useInViewEffect,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useMemo, useRef, useState} from 'react';
import {AvailabilityInfo} from '../../../shared/AvailabilityInfo';
import {ClaimNowButton} from '../../../shared/ClaimNowButton';
import {ExpiryLabel} from '../../../shared/ExpiryLabel';
import {CoinsMissionInfo} from './CoinsMissionInfo';
import {FramedMissionBadge} from './FramedMissionBadge/FramedMissionBadge';

export const CoinsMission: React.FC<
  MissionDetail & {['data-testid']?: string}
> = ({
  'data-testid': dataTestId = 'coinsMission',
  badge,
  summary,
  info,
  bottomSheet,
  expiryLabel,
  missionStatus,
  missionIdentifier,
}) => {
  const missionState = missionStatus.status?.$case ?? '';
  const trackFeature = useTrackFeature();
  const trackComponent = useTrackComponent();
  const hasBeenInView = useRef(false);
  const isPending = missionState === 'pending';
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  const trackMissionTile = (action: 'impression' | 'click') => {
    trackFeature({
      eventType: action,
      label: `${missionIdentifier.missionId}`,
      name: 'missions_tile',
    });
    trackComponent({
      action,
      content_type: 'card',
      name: 'missions_tile',
      context: missionState,
      object_type: 'mission',
      object_id: missionIdentifier.missionId,
    });
  };

  const {ref} = useInViewEffect<HTMLDivElement>(
    inView => {
      if (inView && !hasBeenInView.current) {
        hasBeenInView.current = true;
        trackMissionTile('impression');
      }
    },
    {triggerOnce: true},
  );

  const handleOpenMissionInfo = () => {
    trackMissionTile('click');

    if (bottomSheet) {
      setIsInfoOpen(true);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenMissionInfo();
    }
  };

  const expirationHint = useMemo(() => {
    if (info?.$case === 'claimInfo') {
      return info.claimInfo.untilLabel;
    }

    return expiryLabel;
  }, [expiryLabel, info]);

  const badgeLabel = badge.label;
  const isFramedBadge =
    badge.style === MissionDetail_MissionBadge_BadgeStyle.FRAMED;

  return (
    <>
      <Wrapper
        ref={ref}
        data-testid={dataTestId}
        data-state={missionStatus.status?.$case}
        role="button"
        tabIndex={bottomSheet ? 0 : -1}
        $canClick={Boolean(bottomSheet)}
        onClick={handleOpenMissionInfo}
        onKeyDown={handleKeyDown}
      >
        <TopRow>
          <ImageWrapper>
            {isFramedBadge ? (
              <FramedMissionBadge image={badge.image} />
            ) : (
              <StyledImage
                src={badge.image.url}
                breakpoints={[{width: '128px'}]}
                ratio="1:1"
                widths={[64, 128, 256]}
              />
            )}
          </ImageWrapper>
          <TextSection>
            <Text size="px12">{summary.title}</Text>
            {summary.description && (
              <DescriptionText
                size="px10"
                color="Text.Grey"
                weight="mediumBold"
              >
                {summary.description}
              </DescriptionText>
            )}
          </TextSection>
          <CoinsMissionIcon missionStatus={missionStatus} />
        </TopRow>
        {(info || badgeLabel) && (
          <BottomRow>
            <Reward>
              <Text size="px12" weight="mediumBold">
                {badgeLabel}
              </Text>
              <Icon
                asset={
                  isPending
                    ? GenericIconName.CoinsStriped
                    : GenericIconName.CoinsDoubleColor
                }
                size="XS"
              />
            </Reward>
            {info?.$case === 'progressBar' && (
              <>
                <ProgressBarWrapper>
                  <StyledProgressBar
                    color={COLORS['Fill.Grey.Secondary']}
                    count={info.progressBar.percentage}
                    height={4}
                    total={100}
                  />
                </ProgressBarWrapper>
                <ProgressLabel size="px10" color="Text.Grey">
                  {info.progressBar.label}
                </ProgressLabel>
              </>
            )}
            {info?.$case === 'hint' && (
              <AvailabilityInfo info={info.hint.hint} />
            )}
            {info?.$case === 'claimInfo' && (
              <ClaimNowButton
                label={info.claimInfo.ctaLabel}
                missionIdentifier={missionIdentifier}
                onBeforeClick={() => trackMissionTile('click')}
              />
            )}
          </BottomRow>
        )}
        {expirationHint && <ExpiryLabel expiryLabel={expirationHint} />}
      </Wrapper>
      {bottomSheet && (
        <CoinsMissionInfo
          isOpen={isInfoOpen}
          onClose={() => setIsInfoOpen(false)}
          {...bottomSheet}
        />
      )}
    </>
  );
};

const CoinsMissionIcon: React.FC<{
  missionStatus: MissionDetail_MissionStatus;
}> = ({missionStatus}) => {
  switch (missionStatus.status?.$case) {
    case 'pending':
      return (
        <Icon
          asset={GenericIconName.Clock}
          fill={COLORS['Fill.Grey.Secondary']}
          size="SS"
        />
      );
    case 'complete':
      return (
        <Icon
          asset={GenericIconName.CheckmarkBorder}
          fill={COLORS['Fill.Grey.Secondary']}
          size="SS"
        />
      );
    case 'claimable':
      return null;
    case 'active':
    default:
      return (
        <Icon
          asset={GenericIconName.ArrowRight}
          fill={COLORS['Fill.Black']}
          size="XS"
        />
      );
  }
};

const Wrapper = styled.div<{$canClick: boolean}>`
  display: flex;
  flex-direction: column;
  padding-top: ${SPACING['Y.Md']};
  padding-bottom: ${SPACING['Y.Lg']};
  cursor: ${props => (props.$canClick ? 'pointer' : 'default')};

  &:not(:last-child) {
    border-bottom: 1px solid ${COLORS['Outline.Grey']};
  }

  &[data-state='complete'] {
    padding-bottom: ${SPACING['Y.Md']};
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
`;

const ImageWrapper = styled.div`
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  position: relative;
  overflow: hidden;

  [data-state='complete'] & {
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent 0%,
        transparent 30%,
        rgba(255, 255, 255, 0.3) 50%,
        transparent 70%,
        transparent 100%
      );
      animation: shimmer 3s infinite;
      pointer-events: none;
      z-index: 1;
    }
  }

  @keyframes shimmer {
    0% {
      transform: translateX(-100%) skewX(-20deg);
    }
    50%,
    100% {
      transform: translateX(100%) skewX(-20deg);
    }
  }
`;

const StyledImage = styled(Image)`
  width: 100%;
  height: 100%;
  padding: 2px;
`;

const TextSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  /* Without min-width: 0 the flex item refuses to shrink below an
     unbreakable word (min-width: auto), pushing the row past the wallet's
     edge and making it horizontally pannable on touch devices. */
  min-width: 0;
  overflow-wrap: break-word;
  margin: 0 ${SPACING['X.Lg']} 0 ${SPACING['X.Md']};

  > span {
    line-height: 1.6;
  }
`;

const DescriptionText = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Reward = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 64px;
  gap: ${SPACING['X.Sm']};
`;

const BottomRow = styled.div`
  display: flex;
  align-items: center;
`;

const ProgressBarWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  margin: 0 ${SPACING['X.Lg']} 0 ${SPACING['X.Md']};
`;

const StyledProgressBar = styled(ProgressBar)`
  flex: 1;
`;

/* Block (not flex) so text-overflow can render the ellipsis; vertical
   centering comes from BottomRow's align-items. min-width: 0 lets the
   nowrap label shrink instead of widening the row beyond the wallet,
   which shows up as horizontal scroll on touch devices. */
const ProgressLabel = styled(Text)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: right;
`;
